import React, {useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View, type LayoutChangeEvent} from 'react-native';
import {useTranslation} from 'react-i18next';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {useClientPage} from '@/shared/lib/use-client-page';
import {
  useTableSort,
  type SortAccessors,
  type SortState,
  type SortValue,
} from '@/shared/lib/table-sort';
import {FilterSelect} from '@/shared/ui/filter-bar';
import {Icon} from '@/shared/ui/icons';
import {AppInput} from '@/shared/ui/input';
import {Pager} from '@/shared/ui/pager';
import {DataTable, type Column} from '@/shared/ui/table';
import {AppText} from '@/shared/ui/text';
import {controls, radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Список кабинета — один компонент на все таблицы, и это shadcn-блок `data-table`:
 *
 *      [🔍 Поиск…] [Отдел · Все ▾] [Лицо · Все ▾] [× Сбросить]        [Массово] [+ Добавить]
 *      ┌──────────────────────────────────────────────────────────────────────────────┐
 *      │ Имя ▲          Табельный   Должность        Отдел        Лицо        Приход  │
 *      ├──────────────────────────────────────────────────────────────────────────────┤
 *      │ …                                                                            │
 *      └──────────────────────────────────────────────────────────────────────────────┘
 *      1–20 из 134 · без лица: 3                       Строк: [20|50|100]   ‹ 1 / 7 ›
 *
 * ## Что он делает сам
 *
 * Порядок (`useTableSort`), страницы (`useClientPage`), выбор формы по ширине окна,
 * полосу над таблицей, подвал с рейкой и пустые состояния. Вызывающему остаётся то,
 * что у каждого списка своё: чем фильтровать, как выглядит колонка и карточка.
 *
 * ## Полоса над таблицей — одна строка, и у каждого края своя работа
 *
 * Слева — то, чем СУЖАЮТ: поиск, пилюли фильтров, «Сбросить», когда что-то сужено.
 * Справа — то, что ДЕЛАЮТ: второстепенные глаголы обводкой и ОДИН залитый. Раньше
 * глаголы стояли слева от поиска плоскими подписями, все одного веса, и «Подключить
 * устройство · Обновить · С десктопами · Массовые действия» читалось как меню.
 *
 * На телефоне полосы две и они короткие: поиск во всю ширину, под ним — одна
 * прокручиваемая строка пилюль (порядок, фильтры). Глаголы там НЕ рисуются: их несёт
 * шапка страницы, где им и место под большим пальцем, — иначе та же кнопка стояла на
 * экране дважды.
 *
 * ## Чего здесь больше нет
 *
 * Подсказки «Нажмите на строку — откроется карточка. Заголовок столбца сортирует».
 * Строка выглядит нажимаемой (курсор, подсветка), заголовок — стрелкой, а фраза над
 * каждой таблицей кабинета была первым, что видел читатель, и последним, что ему
 * было нужно. Проп `hint` принимается ради старых вызовов и не рисуется.
 *
 * ## Чего он НЕ делает
 *
 * Не фильтрует. Отбор — это вопрос конкретного экрана, и он часто живёт в адресе:
 * см. `shared/lib/use-param-state`. Компонент получает уже отобранные строки и
 * `total` — сколько их было до отбора, чтобы отличить «здесь пусто» от «поиск ничего
 * не нашёл».
 */
export type DataListProps<T> = {
  /** Строки ПОСЛЕ отбора, до сортировки и разбивки по страницам. */
  rows: readonly T[];
  /** Сколько строк было до отбора. Опущено — берётся длина `rows`. */
  total?: number;
  columns: Column<T>[];
  keyOf: (row: T) => string;
  /** Как достать значение каждого сортируемого столбца. */
  accessors: SortAccessors<T>;
  initialSort: SortState;
  /** Вторичный ключ. Ссылка обязана быть стабильной — см. `useTableSort`. */
  tiebreak?: (row: T) => SortValue;
  /** Порядок снаружи — обычно из адреса страницы. Без него живёт внутри. */
  sortControl?: {value: SortState; onChange: (next: SortState) => void};
  /** Карточка для телефона: та же строка, где столбцов нет. */
  renderCard: (row: T) => React.ReactNode;
  /** Какие порядки предлагать на телефоне. Пусто — пилюли порядка нет. */
  phoneSorts?: readonly string[];
  /** Подпись порядка для ключа сортировки. */
  sortLabel?: (key: string) => string;
  onRowPress?: (row: T) => void;
  selectedKey?: string | null;
  /** Глаголы списка — правый край полосы на широком окне. На телефоне не рисуются. */
  actions?: React.ReactNode;
  /** Строка поиска. Опущена — поля нет. */
  search?: {value: string; onChange: (next: string) => void; placeholder: string};
  /** Пилюли отбора. */
  filters?: React.ReactNode;
  /** Сужен ли список чем-то, кроме поиска, — тогда рядом с пилюлями стоит «Сбросить». */
  dirty?: boolean;
  /** Снять все отборы. Без него «Сбросить» не рисуется. */
  onReset?: () => void;
  /** Правый край полосы — обычно `Explain` со словарём столбцов. */
  aside?: React.ReactNode;
  /** @deprecated Подсказки над таблицей больше не рисуются. */
  hint?: {table: string; cards: string};
  /** Счётчики выборки — рядом с диапазоном в подвале. */
  footer?: React.ReactNode;
  /** Данные ещё едут: вместо строк показывается `skeleton`, обвес остаётся на месте. */
  loading?: boolean;
  skeleton?: React.ReactNode;
  /** «Данных нет вовсе» и «отбор ничего не нашёл». */
  empty: React.ReactNode;
  noMatches: React.ReactNode;
  perPage?: number;
  perPageOptions?: readonly number[];
  pageControl?: {value: number; onChange: (next: number) => void};
  perPageControl?: {value: number; onChange: (next: number) => void};
  /** Сколько карточек в ряд на узком окне. По умолчанию одна. */
  cardColumns?: number;
  /** Короткий список в узкой колонке: ни поиска, ни рейки — только карточки. */
  compact?: boolean;
  testID?: string;
};

const PAGE_SIZE = 25;
const PAGE_SIZES = [25, 50, 100] as const;

/**
 * Пороги `minWidth` у столбцов записаны в ширине ОКНА («убрать табельный номер уже
 * 1280»). Но сетка не всегда получает окно целиком: рядом может стоять инспектор, и
 * тогда столбцы надо ронять по ширине САМОЙ сетки. Ширина окна восстанавливается из
 * измеренной ширины сетки прибавлением рамы — боковой панели и двух полей страницы,
 * — так что старые пороги значат то же самое, а сетка в 700pt рядом с открытой
 * карточкой честно роняет столбцы, вместо того чтобы резать имена до «Алишер Кари…».
 */
const FRAME_WIDTH = 256 + 2 * 32;

const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  /* Широкое окно: одна строка. Переносится только если совсем не помещается. */
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(2),
  },
  toolbarSearch: {width: 260, flexShrink: 0},
  /* `marginLeft: auto` — глаголы прижаты к правому краю и ОСТАЮТСЯ у него, когда
     строка переносится: пустой распорке на новой строке прижимать было бы нечего. */
  toolbarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    marginLeft: 'auto',
  },
  /* Телефон: поиск сверху, пилюли — одной прокручиваемой строкой под ним. */
  toolbarPhone: {gap: spacing(3)},
  pillRow: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), paddingRight: spacing(4)},
  reset: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    height: controls.md,
    paddingHorizontal: spacing(2) + 2,
    borderRadius: radii.md,
  },
  resetPhone: {height: controls.lg},
  cards: {gap: spacing(2)},
  cardGrid: {flexDirection: 'row', flexWrap: 'wrap'},
  pager: {paddingTop: spacing(1)},
});

/** Курсор над контролом или палец на нём. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

export function DataList<T>({
  rows,
  total,
  columns,
  keyOf,
  accessors,
  initialSort,
  tiebreak,
  sortControl,
  renderCard,
  phoneSorts,
  sortLabel,
  onRowPress,
  selectedKey,
  actions,
  search,
  filters,
  dirty = false,
  onReset,
  aside,
  footer,
  loading = false,
  skeleton,
  empty,
  noMatches,
  perPage = PAGE_SIZE,
  perPageOptions = PAGE_SIZES,
  pageControl,
  perPageControl,
  cardColumns = 1,
  compact = false,
  testID,
}: DataListProps<T>) {
  const {t} = useTranslation();
  const p = useTheme();
  const {isDesktop, width} = useBreakpoint();
  const [gridWidth, setGridWidth] = useState<number | null>(null);
  const onGridLayout = (e: LayoutChangeEvent) => {
    const w = Math.round(e.nativeEvent.layout.width);
    setGridWidth(prev => (prev === w ? prev : w));
  };

  const sorted = useTableSort(rows, accessors, initialSort, tiebreak, sortControl);

  const [ownPageSize, setOwnPageSize] = useState<number>(perPage);
  const pageSize = perPageControl ? perPageControl.value : ownPageSize;
  const setPageSize = perPageControl ? perPageControl.onChange : setOwnPageSize;
  const paged = useClientPage(sorted.rows, pageSize, pageControl);
  const pageRows = compact ? sorted.rows : paged.rows;

  const grid = isDesktop && !compact;
  const {key: sortKey, direction} = sorted.state;

  const reset =
    dirty && onReset ? (
      <Pressable
        testID={testID ? `${testID}-reset` : undefined}
        accessibilityRole="button"
        accessibilityLabel={t('actions.reset')}
        onPress={onReset}
        style={state => [
          styles.reset,
          !isDesktop && styles.resetPhone,
          isHot(state) && {backgroundColor: p.accent},
        ]}>
        <AppText variant="label">{t('actions.reset')}</AppText>
        <Icon name="close" size={12} color={p.foreground} />
      </Pressable>
    ) : null;

  const searchField = search ? (
    <AppInput
      label=""
      icon="search"
      size={isDesktop ? 'md' : 'lg'}
      value={search.value}
      onChangeText={search.onChange}
      placeholder={search.placeholder}
      autoCapitalize="none"
      style={grid ? styles.toolbarSearch : undefined}
      testID={testID ? `${testID}-search` : undefined}
    />
  ) : null;

  /*
    Порядок на телефоне — одна пилюля «Сортировка · Имя ↑», а не ряд чипов на всю
    ширину: чипы занимали строку сами по себе, и вместе с поиском и фильтрами это
    была четвёртая полоса контролов над первой карточкой. Нажатие на текущий ключ
    переворачивает направление — ровно так же, как нажатие на активный заголовок.
  */
  const sortPill =
    !grid && phoneSorts && phoneSorts.length > 0 && !compact ? (
      <FilterSelect
        label={t('actions.sort')}
        value={sortKey}
        // Порядок всегда какой-то есть — пилюля не «сужает» и не красится.
        neutral={sortKey}
        phone
        options={phoneSorts.map(key => ({
          value: key,
          label: `${sortLabel ? sortLabel(key) : key}${
            key === sortKey ? (direction === 'asc' ? ' ↑' : ' ↓') : ''
          }`,
        }))}
        onChange={key => sorted.sort.onChange(key)}
        testID={testID ? `${testID}-sort` : undefined}
      />
    ) : null;

  const hasToolbar = !compact && (!!search || !!filters || !!aside || !!actions || !!sortPill);

  const toolbar = !hasToolbar ? null : grid ? (
    <View style={styles.toolbar}>
      {searchField}
      {filters}
      {reset}
      {aside}
      {actions ? <View style={styles.toolbarActions}>{actions}</View> : null}
    </View>
  ) : (
    <View style={styles.toolbarPhone}>
      {searchField}
      {sortPill || filters || reset || aside ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={styles.pillRow}>
          {sortPill}
          {filters}
          {reset}
          {aside}
        </ScrollView>
      ) : null}
    </View>
  );

  // Пусто ДО отбора — это про данные, и никакой поиск тут не поможет.
  if (!loading && (total ?? rows.length) === 0) {
    return <>{empty}</>;
  }

  return (
    <View style={styles.root} testID={testID}>
      {toolbar}

      {loading ? (
        <>{skeleton}</>
      ) : sorted.rows.length === 0 ? (
        <>{noMatches}</>
      ) : grid ? (
        <View onLayout={onGridLayout}>
          <DataTable
            testID={testID ? `${testID}-table` : undefined}
            columns={columns}
            rows={pageRows}
            width={gridWidth != null ? gridWidth + FRAME_WIDTH : width}
            keyOf={keyOf}
            onRowPress={onRowPress}
            selectedKey={selectedKey ?? null}
            sort={sorted.sort}
          />
        </View>
      ) : (
        <View style={[styles.cards, cardColumns > 1 && styles.cardGrid]}>
          {pageRows.map(row => (
            <View
              key={keyOf(row)}
              style={cardColumns > 1 ? {width: `${100 / cardColumns}%`} : undefined}>
              {renderCard(row)}
            </View>
          ))}
        </View>
      )}

      {/* Рейка страниц сразу под данными: её видит тот же взгляд, который дочитал
          последнюю строку. Счётчики списка стоят в ней же, слева. */}
      {compact || loading || sorted.rows.length === 0 ? null : (
        <Pager
          page={paged.page}
          totalPages={paged.totalPages}
          total={paged.total}
          perPage={paged.perPage}
          onPage={paged.setPage}
          perPageOptions={perPageOptions}
          onPerPage={setPageSize}
          style={styles.pager}
          testID={testID ? `${testID}-pager` : 'pager'}>
          {footer}
        </Pager>
      )}
    </View>
  );
}
